import { translations, type SupportedLanguage } from "../locales";

export const DUBBING_LANDING_PATH = "/ai-video-voice-over-dubbing" as const;
export const DUBBING_PAIR_PATH_PREFIX = `${DUBBING_LANDING_PATH}-english-to-` as const;
export const DUBBING_PAIR_ENGLISH_TO_SPANISH_PATH = `${DUBBING_PAIR_PATH_PREFIX}spanish` as const;
export const SPANISH_DUBBING_LANDING_PATH = "/es/doblaje-video-ia" as const;
export const SPANISH_DUBBING_PAIR_ENGLISH_TO_SPANISH_PATH =
  "/es/traduce-video-doblaje-ia-espanol-ingles" as const;
const LOCALIZED_HTML_PATH_RE = /^\/(.+)-([a-z]{2})\.html$/i;
const LOCALIZED_CLEAN_PATH_RE = /^\/(.+)-([a-z]{2})$/i;
const BLOG_ARTICLE_EN_RE = /^\/blog\/([^/]+)$/i;
const BLOG_ARTICLE_LOCALIZED_RE = /^\/([a-z]{2})\/blog\/([^/]+)$/i;

export type LocalizedBasePath = string;

const englishBasePaths: string[] = [
  "/",
  "/blog",
  "/privacy",
  "/terms",
  DUBBING_LANDING_PATH,
];

const nonRootBasePathSet = new Set<string>(englishBasePaths.filter((path) => path !== "/"));

const prefixToBasePath: Record<string, string> = {
  index: "/",
  blog: "/blog",
  privacy: "/privacy",
  terms: "/terms",
  "ai-video-voice-over-dubbing": DUBBING_LANDING_PATH,
};

const spanishSeoAliases: Record<string, LocalizedBasePath> = {
  [SPANISH_DUBBING_LANDING_PATH]: DUBBING_LANDING_PATH,
  [SPANISH_DUBBING_PAIR_ENGLISH_TO_SPANISH_PATH]: DUBBING_PAIR_ENGLISH_TO_SPANISH_PATH,
};

function normalizePathname(pathname: string): string {
  if (!pathname) return "/";
  if (pathname === "/") return "/";
  return pathname.endsWith("/") ? pathname.slice(0, -1) : pathname;
}

export function getBlogArticleSlugFromUrlPath(pathname: string): string | null {
  const normalizedPathname = normalizePathname(pathname);
  const localizedMatch = normalizedPathname.match(BLOG_ARTICLE_LOCALIZED_RE);

  if (localizedMatch) {
    const [, lang, slug] = localizedMatch;
    if (isSupportedLanguage(lang.toLowerCase())) {
      return decodeURIComponent(slug);
    }
  }

  const englishMatch = normalizedPathname.match(BLOG_ARTICLE_EN_RE);
  if (englishMatch) {
    return decodeURIComponent(englishMatch[1]);
  }

  return null;
}

export function isSupportedLanguage(value?: string | null): value is SupportedLanguage {
  return Boolean(value) && Object.prototype.hasOwnProperty.call(translations, value);
}

export function getLocalizedPath(path: LocalizedBasePath, language: SupportedLanguage): string {
  if (language === "es") {
    if (path === DUBBING_LANDING_PATH) {
      return `${SPANISH_DUBBING_LANDING_PATH}/`;
    }
    if (path === DUBBING_PAIR_ENGLISH_TO_SPANISH_PATH) {
      return `${SPANISH_DUBBING_PAIR_ENGLISH_TO_SPANISH_PATH}/`;
    }
  }

  if (language === "en") {
    return path;
  }

  if (path === "/") {
    return `/index-${language}.html`;
  }

  return `${path}-${language}.html`;
}

export function isDubbingPairPath(pathname: string): boolean {
  return (
    pathname.startsWith(DUBBING_PAIR_PATH_PREFIX) &&
    pathname.length > DUBBING_PAIR_PATH_PREFIX.length &&
    !pathname.includes(".html") &&
    !pathname.includes("?") &&
    !pathname.includes("#")
  );
}

export function getBasePathFromUrlPath(pathname: string): LocalizedBasePath | null {
  const normalizedPathname = normalizePathname(pathname);

  if (getBlogArticleSlugFromUrlPath(normalizedPathname)) {
    return "/blog";
  }

  if (spanishSeoAliases[normalizedPathname]) {
    return spanishSeoAliases[normalizedPathname];
  }

  if (englishBasePaths.includes(normalizedPathname) || isDubbingPairPath(normalizedPathname)) {
    return normalizedPathname;
  }

  const match =
    normalizedPathname.match(LOCALIZED_HTML_PATH_RE) ||
    normalizedPathname.match(LOCALIZED_CLEAN_PATH_RE);
  if (!match) {
    return null;
  }

  const [, prefix, lang] = match;
  if (!isSupportedLanguage(lang.toLowerCase())) {
    return null;
  }

  if (prefix.toLowerCase() === "index") {
    return "/";
  }

  const basePath = `/${prefix.toLowerCase()}`;
  if (nonRootBasePathSet.has(basePath) || isDubbingPairPath(basePath)) {
    return basePath;
  }

  return prefixToBasePath[prefix.toLowerCase()] || null;
}

export function getLanguageFromUrlPath(pathname: string): SupportedLanguage | null {
  const normalizedPathname = normalizePathname(pathname);
  const localizedBlogArticleMatch = normalizedPathname.match(BLOG_ARTICLE_LOCALIZED_RE);

  if (localizedBlogArticleMatch) {
    const [, lang] = localizedBlogArticleMatch;
    return isSupportedLanguage(lang.toLowerCase()) ? lang.toLowerCase() : null;
  }

  if (BLOG_ARTICLE_EN_RE.test(normalizedPathname)) {
    return "en";
  }

  if (spanishSeoAliases[normalizedPathname]) {
    return "es";
  }

  const match =
    normalizedPathname.match(LOCALIZED_HTML_PATH_RE) ||
    normalizedPathname.match(LOCALIZED_CLEAN_PATH_RE);
  if (!match) {
    return null;
  }

  const [, prefix, langCode] = match;
  const normalizedLanguage = langCode.toLowerCase();
  if (!isSupportedLanguage(normalizedLanguage)) {
    return null;
  }

  if (prefix.toLowerCase() === "index") {
    return normalizedLanguage;
  }

  const basePath = `/${prefix.toLowerCase()}` as LocalizedBasePath;
  return nonRootBasePathSet.has(basePath) || isDubbingPairPath(basePath) ? normalizedLanguage : null;
}

export function isDubbingLandingPath(pathname: string): boolean {
  return getBasePathFromUrlPath(pathname) === DUBBING_LANDING_PATH;
}

export function isDubbingLandingFamilyPath(pathname: string): boolean {
  const basePath = getBasePathFromUrlPath(pathname);
  return basePath === DUBBING_LANDING_PATH || (basePath ? isDubbingPairPath(basePath) : false);
}
